import React, { useState, useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { getAvailableLanguages, setReviewHasError } from "actions/signup";
import moment from "moment";
import Dropdown from "components/Dropdown/Dropdown";
import { Countries } from "consts/countries";
import ReviewFormRow from "./ReviewFormRow";
import SectionHead from "./SectionHead";
import TextInput from "components/Form/TextInput";
import InputGroup from "components/Form/InputGroup";
import { InfoIcon } from "lucide-react";
import { MailIcon } from "lucide-react";
import ManualSocialEntryInput from "../FormSteps/ManualSocialEntryInput";
import get from "lodash/get";

const NameFormRow = ({
  formData,
  onChange,
  onBlur,
  hasError,
  setErrorMessage,
  errorMessage,
}) => (
  <div className="flex items-start justify-start space-x-2">
    <div className="w-1/2">
      <TextInput
        type="text"
        value={formData?.first_name}
        onChange={(value) => {
          setErrorMessage({ ...errorMessage, first_name: "" });
          onChange({ ...formData, first_name: value.trim() });
        }}
        placeholder="First name"
        onBlur={onBlur}
        hasError={
          get(errorMessage, "first_name", "")
            ? get(errorMessage, "first_name", "")
            : null
        }
      />
    </div>
    <div className="w-1/2">
      <TextInput
        type="text"
        value={formData?.last_name}
        onChange={(value) => {
          setErrorMessage({ ...errorMessage, last_name: "" });
          onChange({ ...formData, last_name: value.trim() });
        }}
        placeholder="Last name"
        onBlur={onBlur}
        hasError={
          get(errorMessage, "last_name", "")
            ? get(errorMessage, "last_name", "")
            : null
        }
      />
    </div>
  </div>
);

const EmailFormRow = ({ formData, onChange, hasError, errorMessage }) => {
  return (
    <InputGroup
      id="email"
      name="email"
      type="email"
      placeholder="you@example.com"
      value={formData.email}
      onChange={(value) =>
        onChange({
          ...formData,
          email: value,
        })
      }
      Icon={() => <MailIcon className="h-5 w-5 text-gray-400" />}
      hasError={hasError ? get(errorMessage, "email", "") : null}
    />
  );
};

const DisplayNameFormRow = ({
  formData,
  onChange,
  onBlur,
  hasError,
  errorMessage,
}) => {
  return (
    <TextInput
      type="text"
      value={formData.display_name}
      onBlur={onBlur}
      onChange={(value) => onChange({ ...formData, display_name: value })}
      placeholder="Display name"
      hasError={hasError ? get(errorMessage, "display_name", "") : null}
    />
  );
};

const BirthdayFormRow = ({
  formData,
  onChange,
  onBlur,
  hasError,
  errorMessage,
}) => {
  return (
    <TextInput
      type="date"
      value={formData.birthday}
      placeholder="mm/dd/yyyy"
      onBlur={onBlur}
      onChange={(value) => onChange({ ...formData, birthday: value })}
      hasError={hasError ? get(errorMessage, "birthday", "") : null}
    />
  );
};

const PhoneFormRow = ({
  formData,
  onChange,
  onBlur,
  hasError,
  errorMessage,
}) => {
  return (
    <TextInput
      type="tel"
      value={formData.phone}
      onBlur={onBlur}
      onChange={(value) => onChange({ ...formData, phone: value })}
      placeholder="Phone number"
      hasError={hasError ? get(errorMessage, "phone", "") : null}
    />
  );
};

const ReviewApplicant = ({
  onChange,
  onSubmit,
  hasAccountConnected,
  formData,
  goToStep,
}) => {
  const dispatch = useDispatch();
  const [hasError, setHasError] = useState(false);
  const [errorMessage, setErrorMessage] = useState({});
  const [languagesOptions, setLanguagesOptions] = useState([]);
  const userLanguages = useSelector((state) => state.signup.languages || []);
  // const reviewHasError = useSelector(
  //   (state) => state.signup.reviewHasError || null
  // );
  const signupToken = useSelector((state) => state.signup.signupToken || null);
  // Social Channels
  // const fetchingUserRegistration = useSelector(
  //   (state) => state?.signup?.fetchingUserRegistration || false
  // );
  const connectedChannels = useSelector(
    (state) => state?.signup?.rewardsUser || []
  );

  // Form Validations
  const onNameValidation = () => {
    let result = {};
    let hasInvalidInput = false;
    const notAllowedChars = /[`!@#$%^&*()_+\-=\[\]{};':"\\|,<>\/?~0123456789]/;
    const notAllowedOnlyDots = /^\.+$/;
    const firstName = get(formData, "first_name", "");
    const lastName = get(formData, "last_name", "");

    if (
      !firstName ||
      notAllowedChars.test(firstName) ||
      notAllowedOnlyDots.test(firstName)
    ) {
      hasInvalidInput = true;
      result.first_name = !firstName
        ? "Please enter a first name"
        : "Invalid entry. Special characters & numbers not allowed.";
    }

    if (
      !lastName ||
      notAllowedChars.test(lastName) ||
      notAllowedOnlyDots.test(lastName)
    ) {
      hasInvalidInput = true;
      result.last_name = !lastName
        ? "Please enter a last name"
        : "Invalid entry. Special characters & numbers not allowed.";
    }

    setHasError(hasInvalidInput);
    setErrorMessage(result);
    return hasInvalidInput;
  };

  const onBirthdayValidation = () => {
    let result = {};
    let hasInvalidInput = false;
    const birthday = get(formData, "birthday", "");
    const isDateValid =
      /^(0[1-9]|1[012])[- /.](0[1-9]|[12][0-9]|3[01])[- /.](19|20)\d\d$/;

    if (
      !birthday ||
      !isDateValid.test(moment(birthday).format("MM/DD/YYYY")) ||
      (isDateValid.test(moment(birthday).format("MM/DD/YYYY")) &&
        moment(birthday).isSameOrAfter(moment()))
    ) {
      hasInvalidInput = true;
      result.birthday = !birthday
        ? "Please enter a valid birthday."
        : "Invalid date range.";
    } else {
      hasInvalidInput = false;
      result.birthday = null;
    }

    setErrorMessage({ ...errorMessage, ...result });
    return hasInvalidInput;
  };

  const onDisplayNameValidation = () => {
    const displayName = get(formData, "display_name", "");
    if (!displayName) {
      setErrorMessage({
        ...errorMessage,
        display_name: "Please enter display name",
      });
      return true;
    }
    setErrorMessage({ ...errorMessage, display_name: "" });
    return false;
  };

  const onPhoneValidation = () => {
    const phone = get(formData, "phone", "");
    if (!phone || phone.length <= 6) {
      setErrorMessage({
        ...errorMessage,
        phone: "Please enter a phone number",
      });
      return true;
    }
    setErrorMessage({ ...errorMessage, phone: "" });
    return false;
  };

  const onCountryValidation = (value) => {
    const country = get(formData, "country", "");
    if (!country) {
      setErrorMessage({
        ...errorMessage,
        country: "Please select a country",
      });
      return true;
    }
    setErrorMessage({ ...errorMessage, country: "" });
    return false;
  };

  const onLanguageValidation = (value) => {
    const language = value || get(formData, "language", "");
    if (!language) {
      setErrorMessage({
        ...errorMessage,
        language: "Please select a language",
      });
      return true;
    }
    setErrorMessage({ ...errorMessage, language: "" });
    return false;
  };

  useEffect(() => {
    dispatch(getAvailableLanguages());
  }, []);

  useEffect(() => {
    if (!userLanguages?.length) return;
    if (languagesOptions?.length) return;

    setLanguagesOptions(
      userLanguages.map((lang) => {
        return {
          value: lang.id,
          label: lang.label,
        };
      })
    );
  }, [userLanguages]);

  useEffect(() => {
    if (Object.keys(errorMessage)?.length) {
      dispatch(setReviewHasError(errorMessage));
    }
  }, [errorMessage]);

  const manualSocialEntries = useMemo(() => {
    const filterManualChannels = connectedChannels?.social_channels?.filter(
      (c) => c?.phyllo !== true
    );
    if (!filterManualChannels?.length) return;
    return {
      youtube: (filterManualChannels ?? []).find(
        (c) => c?.channel === "YouTube" && !c?.phyllo
      ),
      instagram: (filterManualChannels ?? []).find(
        (c) => c?.channel === "Instagram" && !c?.phyllo
      ),
      tiktok: (filterManualChannels ?? []).find(
        (c) => c?.channel === "TikTok" && !c?.phyllo
      ),
    };
  }, [connectedChannels]);

  return (
    <section>
      <SectionHead
        title="Applicant Information"
        subtitle="Personal details and application"
      />
      <div className="divide-y divide-gray-200">
        <ReviewFormRow
          label="Full name"
          RowData={() => (
            <span>{`${formData?.first_name} ${formData?.last_name}`}</span>
          )}
          onChange={(value) => {
            onChange(value, setErrorMessage);
          }}
          onClick={() => {
            dispatch(
              onSubmit(
                {
                  signup_token: signupToken,
                  rewards_user: {
                    first_name: formData.first_name,
                    last_name: formData.last_name,
                    email: formData.email,
                  },
                },
                "registrations"
              )
            );
          }}
          formData={formData}
          EditForm={NameFormRow}
          setErrorMessage={setErrorMessage}
          errorMessage={errorMessage}
          onValidate={onNameValidation}
        />
        <ReviewFormRow
          label="Email address"
          readOnly
          RowData={() => <span>{formData?.email}</span>}
          onChange={onChange}
          formData={formData}
          EditForm={EmailFormRow}
          errorMessage={errorMessage}
        />
        <ReviewFormRow
          label="Display Name"
          RowData={() => <span>{formData?.display_name}</span>}
          onChange={(value) => {
            if (errorMessage.display_name) {
              onDisplayNameValidation();
            }
            onChange(value);
          }}
          onClick={() => {
            dispatch(
              onSubmit(
                {
                  signup_token: signupToken,
                  rewards_user: {
                    display_name: formData.display_name,
                  },
                },
                "registrations"
              )
            );
          }}
          onBlur={() => {
            if (errorMessage.display_name) {
              onDisplayNameValidation();
            }
          }}
          formData={formData}
          EditForm={DisplayNameFormRow}
          errorMessage={errorMessage}
          onValidate={onDisplayNameValidation}
        />
        <ReviewFormRow
          label="Date of Birth"
          RowData={() => (
            <span>
              {formData?.birthday &&
                moment(formData?.birthday).format("MM/DD/YYYY")}
            </span>
          )}
          onChange={(value) => {
            setErrorMessage({ ...errorMessage, birthday: "" });
            onChange(value);
          }}
          onClick={() => {
            dispatch(
              onSubmit(
                {
                  signup_token: signupToken,
                  rewards_user: {
                    birthday: formData.birthday,
                  },
                },
                "registrations"
              )
            );
          }}
          formData={formData}
          EditForm={BirthdayFormRow}
          errorMessage={errorMessage}
          onValidate={onBirthdayValidation}
        />
        <ReviewFormRow
          label="Phone"
          RowData={() => <span>{formData?.phone}</span>}
          onChange={(value) => {
            if (errorMessage.phone) {
              onPhoneValidation();
            }
            onChange(value);
          }}
          onClick={() => {
            dispatch(
              onSubmit(
                {
                  signup_token: signupToken,
                  rewards_user: {
                    phone: formData.phone,
                  },
                },
                "registrations"
              )
            );
          }}
          onBlur={() => {
            if (errorMessage.phone) {
              onPhoneValidation();
            }
          }}
          formData={formData}
          EditForm={PhoneFormRow}
          errorMessage={errorMessage}
          onValidate={onPhoneValidation}
        />
        <ReviewFormRow
          label="Allow MagicLinks to send SMS?"
          onClick={() => {
            dispatch(
              onSubmit(
                {
                  signup_token: signupToken,
                  rewards_user: {
                    sms_opt_in: formData.sms_opt_in,
                  },
                },
                "registrations"
              )
            );
          }}
          RowData={() => (
            <>
              <span className="block mb-1">
                {formData?.sms_opt_in ? "Yes" : "No"}
              </span>
              {!formData?.sms_opt_in && (
                <span className="inline-flex items-center justify-start text-orange-900 bg-orange-50 px-2 py-0.5">
                  <InfoIcon className="h-4 w-4 inline mr-1" />
                  You’re missing out on awesomeness
                </span>
              )}
            </>
          )}
          EditForm={() => (
            <div className="relative flex items-start my-1">
              <div className="flex h-5 items-center">
                <input
                  id="sms"
                  aria-describedby="sms-description"
                  name="sms"
                  type="checkbox"
                  checked={formData?.sms_opt_in}
                  onChange={(event) => {
                    onChange({
                      ...formData,
                      sms_opt_in: event.target.checked,
                    });
                  }}
                  className="h-4 w-4 form-checkbox rounded border-gray-300 text-brand-500 focus:ring-brand-500"
                />
              </div>
              <div className="ml-3 text-sm">
                <label htmlFor="sms" className="font-medium text-gray-700">
                  Allow MagicLinks to send SMS messages
                </label>
                <p id="sms-description" className="text-gray-500">
                  We will notify only important updates and reminders.
                </p>
              </div>
            </div>
          )}
        />
        <ReviewFormRow
          label="Country/Region"
          RowData={() => (
            <span>
              {
                Countries.find((country) => country.value === formData?.country)
                  .label
              }
            </span>
          )}
          EditForm={() => (
            <Dropdown
              placeholder="Please select"
              options={Countries}
              value={Countries.find(
                (country) => country.value === formData?.country
              )}
              isClearable={false}
              onBlur={onCountryValidation}
              onChange={(selected) => {
                if (selected?.value) {
                  onCountryValidation(selected.value);
                  onChange({ ...formData, country: selected.value });
                }
              }}
              error={get(errorMessage, "country", "")}
            />
          )}
          onClick={() => {
            dispatch(
              onSubmit(
                {
                  signup_token: signupToken,
                  rewards_user: {
                    country: formData.country,
                  },
                },
                "registrations"
              )
            );
          }}
          formData={formData}
          errorMessage={errorMessage}
          onValidate={onCountryValidation}
        />
        <ReviewFormRow
          label="Language"
          RowData={() => (
            <span>
              {
                languagesOptions.find(
                  (language) => language.value === formData?.language
                )?.label
              }
            </span>
          )}
          EditForm={() => (
            <Dropdown
              placeholder="Please select"
              options={languagesOptions}
              value={languagesOptions.find(
                (language) => language.value === formData?.language
              )}
              isClearable={false}
              onChange={(selected) => {
                if (selected?.value) {
                  onLanguageValidation(selected.value);
                  onChange({ ...formData, language: selected.value });
                }
              }}
              error={get(errorMessage, "language", "")}
            />
          )}
          onClick={() => {
            dispatch(
              onSubmit(
                {
                  signup_token: signupToken,
                  rewards_user: {
                    language: formData.language,
                  },
                },
                "registrations"
              )
            );
          }}
          formData={formData}
          onValidate={onLanguageValidation}
        />
        <div className="grid grid-cols-12 items-start justify-start w-full min-h-[60px] pt-5">
          <label className="col-span-12 lg:col-span-4 text-sm text-primary-600 font-medium mb-2 lg:mb-0">
            Connected Networks
          </label>
          <div className="col-span-12 lg:col-span-8">
            <div className="space-y-4">
              {manualSocialEntries?.youtube && (
                <ManualSocialEntryInput
                  isReviewPage
                  platform="YouTube"
                  channel={manualSocialEntries?.youtube}
                />
              )}
              {manualSocialEntries?.instagram && (
                <ManualSocialEntryInput
                  isReviewPage
                  platform="Instagram"
                  channel={manualSocialEntries?.instagram}
                />
              )}
              {manualSocialEntries?.tiktok && (
                <ManualSocialEntryInput
                  isReviewPage
                  platform="TikTok"
                  channel={manualSocialEntries?.tiktok}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default ReviewApplicant;
